import { DOCUMENT, Location } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import {
    ApplicationRef,
    EventEmitter,
    Inject,
    Injectable,
    OnDestroy,
    Renderer2,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import { CookieService } from "ngx-cookie-service";
import { Observable, Subscription, switchMap } from "rxjs";
import { delay, first, map, tap } from "rxjs/operators";

import { LivechatConfig } from "@hermes/api-model-live-chat";
import {
    Context,
    LOCALE,
    Settings,
    StorageManager,
    StorageService,
    WINDOW,
} from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { Logger } from "@hermes/logger";
import { getPageType } from "@hermes/states/product-page";
import { AnalyticsService } from "@hermes/utils/analytics";
import { HTTP_JSON_CONTENT_TYPE_HEADER } from "@hermes/utils-generic/constants";
import { generateLink } from "@hermes/utils-generic/helpers";
import { HeadService } from "@hermes/utils-generic/services/head";
import { LoadExternalUrlService } from "@hermes/utils-generic/services/load-external-url";
import { LOGGER } from "@hermes/web-logger";

import {
    AWAY_MESSAGE,
    AWAY_MESSAGE_TIMER,
    CS_MASK,
    DISCONNECT_MESSAGE,
    DISCONNECT_MESSAGE_TIMER,
    ICON_CHEVRON,
    ICON_LIVECHAT,
    ICON_SEND,
    LIVECHAT_ACTION_WEBCHAT_CANCELLED,
    LIVECHAT_ACTION_WEBCHAT_CLOSED,
    LIVECHAT_ACTION_WEBCHAT_COMPLETED,
    LIVECHAT_ACTION_WEBCHAT_END_CHAT,
    LIVECHAT_ACTION_WEBCHAT_INJECT_MESSAGE,
    LIVECHAT_ACTION_WEBCHAT_MESSAGE_ADDED,
    LIVECHAT_ACTION_WEBCHAT_MINIMIZE,
    LIVECHAT_ACTION_WEBCHAT_MINIMIZED,
    LIVECHAT_ACTION_WEBCHAT_OPEN,
    LIVECHAT_ACTION_WEBCHAT_OPENED,
    LIVECHAT_ACTION_WEBCHAT_READY,
    LIVECHAT_ACTION_WEBCHAT_SUBMITTED,
    LIVECHAT_ACTION_WEBCHAT_UNMINIMIZED,
    LIVECHAT_CONFIGURATION,
    LIVECHAT_COOKIE_UNREAD_MESSAGE_COUNT,
    PRIVACY_LINK_KEY,
    PRIVACY_LINK_KEYWORD,
    PRIVACY_LINK_TEXT,
    PRIVACY_TEXT_KEY,
} from "../constants/livechat.constants";
import {
    CLOSE_CHAT,
    DISPLAY_AUTOMATIC_BUBBLE,
    ENGAGED,
    FIRST_MESSAGE,
    LAUNCH_CHAT,
    LivechatEvent,
    SUBMIT_CHAT,
} from "../events/livechat.event";
import {
    CXBusType,
    CXPlugin,
    GenesysConfig,
    LivechatMessage,
    WebchatInjectMessageOptions,
    WebchatMinimizeOptions,
    WebchatOpenOptions,
} from "../livechat.types";

@Injectable()
export class LiveChatService implements OnDestroy {
    public chatWindowIsMinimized: boolean = false;
    public chatStarted: boolean = false;
    public customPlugin!: CXPlugin;
    public isLoaded: boolean = false;
    public newMessage: boolean = false;
    public pageType: string = "";
    public showChatButton: boolean = false;
    public subscriptionTimer: Subscription = new Subscription();
    // eslint mistake there is an unsubscribe in unsubscribeTimers()
    // eslint-disable-next-line rxjs-angular/prefer-composition
    public subscriptionAwayMessageTimer: Subscription = new Subscription();
    // eslint-disable-next-line rxjs-angular/prefer-composition
    public subscriptionDisconnectMessageTimer: Subscription =
        new Subscription();
    public subscription: Subscription = new Subscription();
    public userMessages: number = 0;

    public changeDetectionEmitter: EventEmitter<void> =
        new EventEmitter<void>();

    public awayMessageArgs: WebchatInjectMessageOptions | undefined;
    public disconnectMessageArgs: WebchatInjectMessageOptions | undefined;
    public informativeMessage = $localize`:@@hermes_livechat.informative_message:`;

    private sessionStorage!: StorageManager;

    private livechatConfigUrl: string = "";

    constructor(
        protected context: Context,
        private cookieService: CookieService,
        private httpClient: HttpClient,
        private loadExternalUrlService: LoadExternalUrlService,
        private headService: HeadService,
        private settings: Settings,
        private location: Location,
        private storageService: StorageService,
        private analyticsService: AnalyticsService,
        private translateService: TranslateService,
        /* eslint-disable @typescript-eslint/naming-convention */
        @Inject(WINDOW)
        private window: Window & { CXBus: CXBusType; _genesys: GenesysConfig },
        /* eslint-enable @typescript-eslint/naming-convention */
        @Inject(DOCUMENT) private document: Document,
        @Inject(LOGGER) private logger: Logger,
        @Inject(LOCALE) private locale: Locale,
        private appReference: ApplicationRef,
    ) {
        this.sessionStorage =
            this.storageService.getSessionStorageInstance() as StorageManager;
    }

    /**
     * Lazy-loads the livechat css theme
     */
    public loadCSS(): void {
        this.headService.addTag({
            rel: "stylesheet",
            type: "text/css",
            href: `${this.locale.urlPrefix}/livechat.css`,
        });
    }

    /**
     * Launch chat :
     * Called when click on the button
     *
     */
    public launchChat(): void {
        if (this.chatWindowIsMinimized) {
            // chat already started : we want to unminimized
            this.customPlugin?.command?.(LIVECHAT_ACTION_WEBCHAT_MINIMIZE, {
                minimized: false,
            } as WebchatMinimizeOptions);
            this.changeDetectionEmitter.emit();
        } else {
            this.subscription.add(
                this.getPrivacyMessage().subscribe((privacyMessage) => {
                    const privacyMessageText =
                        this.replaceLineBreaks(privacyMessage);

                    // chat not started yet : we want to open the chat
                    this.customPlugin?.command?.(LIVECHAT_ACTION_WEBCHAT_OPEN, {
                        formJSON: {
                            wrapper: this.informativeMessage
                                ? `<div class="livechat-privacy-text"><p>${this.replaceLineBreaks(
                                      this.informativeMessage,
                                  )}</p>${privacyMessageText}</div>`
                                : `<div class="livechat-privacy-text"><p>${privacyMessageText}</p></div>`,
                        },
                    } as WebchatOpenOptions);
                }),
            );
        }
    }

    public loadLiveChat(renderer: Renderer2, pageType: string): void {
        if (pageType !== undefined) {
            this.pageType = pageType;
        }
        if (this.context.isInBrowserMode()) {
            if (this.isLoaded) {
                if (!this.chatStarted) {
                    this.launchLiveChatLight();
                }
                return;
            }
            this.loadCSS();

            this.subscription.add(
                this.retrieveLivechatKeys()
                    .pipe(
                        switchMap((livechatConfigKeys) => {
                            // eslint-disable-next-line no-underscore-dangle
                            this.window._genesys =
                                this.updateLiveChatConfiguration(
                                    livechatConfigKeys,
                                );
                            this.livechatConfigUrl = livechatConfigKeys.dataUrl;
                            const LIVECHAT_LIBRARY_PATH = `https://apps.${this.livechatConfigUrl}/widgets/9.0/cxbus.min.js`;
                            return this.loadExternalUrlService.loadExternalLibrary(
                                LIVECHAT_LIBRARY_PATH,
                            );
                        }),
                        tap(() => {
                            this.isLoaded = true;
                            this.launchLiveChat(renderer);
                        }),
                    )
                    .subscribe(),
            );

            this.subscription.add(
                this.translateService
                    .get([AWAY_MESSAGE, DISCONNECT_MESSAGE])
                    .pipe(first())
                    .subscribe((translations: Record<string, string>) => {
                        this.awayMessageArgs = {
                            type: "html",
                            text: translations[AWAY_MESSAGE],
                            custom: false,
                            bubble: {
                                time: true,
                            },
                        };
                        this.disconnectMessageArgs = {
                            type: "html",
                            text: translations[DISCONNECT_MESSAGE],
                            custom: false,
                            bubble: {
                                time: true,
                            },
                        };
                    }),
            );
        }
    }

    public updateLiveChatConfiguration(keys: LivechatConfig): GenesysConfig {
        const language = this.locale.langCode;
        const { dataUrl, deploymentKey, orgGuid } = keys;
        const { widgets } = LIVECHAT_CONFIGURATION;
        const { main, webchat } = widgets;

        return {
            ...LIVECHAT_CONFIGURATION,
            widgets: {
                ...widgets,
                webchat: {
                    ...webchat,
                    userData: {
                        storeview: this.locale.code,
                    },
                    transport: {
                        ...webchat.transport,
                        dataURL: `https://api.${dataUrl}`,
                        deploymentKey,
                        orgGuid,
                    },
                },
                main: {
                    ...main,
                    lang: language,
                    timeFormat: language === "en" ? 12 : 24,
                },
            },
        };
    }

    public retrieveLivechatKeys(): Observable<LivechatConfig> {
        return this.httpClient.get<LivechatConfig>(
            `${this.settings.apiUrl}/livechat-config`,
            {
                headers: new HttpHeaders({
                    ...HTTP_JSON_CONTENT_TYPE_HEADER,
                }),
            },
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.subscriptionTimer) {
            this.subscriptionTimer.unsubscribe();
        }
        this.unsubscribeTimers();
    }

    public editIcons(renderer: Renderer2): void {
        this.removeIcons("cx-powered-by");
        this.removeIcons("cx-menu-cell");
        this.removeIcons("cx-button-close");
        this.addIcons(renderer, ".cx-send.cx-icon", ICON_SEND);
        this.addIcons(renderer, ".cx-titlebar > .cx-icon", ICON_LIVECHAT);
        this.addIcons(renderer, ".cx-button-minimize.cx-icon", ICON_CHEVRON);
    }

    public removeIcons(className: string): void {
        // Select class reference
        const removeProperties =
            this.document.getElementsByClassName(className)[0];
        // Remove selected properties
        if (removeProperties) {
            removeProperties.remove();
        }
    }

    public addIcons(renderer: Renderer2, className: string, svg: string): void {
        // Try to overload the css when the live chat starts
        try {
            // Select class reference
            const icon = renderer.selectRootElement(className, true);
            // Add icon in selected properties
            renderer.setProperty(icon, "innerHTML", svg);
        } catch (error) {
            // Livechat css not found, ignoring the error
            this.logger.warn(`Livechat css not found ${error}`);
        }
    }

    public emitChange(): void {
        this.changeDetectionEmitter.emit();
    }

    /**
     * Restore away and disconnect messages after F5 or page change ( until we are in SPA everywhere )
     *
     * @param result : LivechatMessage
     */
    public async restoreMessages(result: LivechatMessage): Promise<void> {
        this.unsubscribeTimers();
        // Time difference between now and when the message was sent
        const timeDiff: number = Date.now() - result.data.message.timestamp;
        const isLastClientMessage: boolean =
            this.sessionStorage.getItem("lastClientMessageTimestamp") ===
            result.data.message.timestamp;
        const isExternalMessage: boolean =
            !this.sessionStorage.getItem("lastClientMessageTimestamp") &&
            result.data?.message?.type === "External" &&
            result.data?.message?.text?.length < 1;

        if (timeDiff < AWAY_MESSAGE_TIMER) {
            this.triggerAwayTimer(timeDiff);
        } else if (
            timeDiff > AWAY_MESSAGE_TIMER &&
            timeDiff < DISCONNECT_MESSAGE_TIMER + AWAY_MESSAGE_TIMER &&
            (isLastClientMessage || isExternalMessage)
        ) {
            this.sendAwayMessage(timeDiff);
        } else if (
            timeDiff > AWAY_MESSAGE_TIMER + DISCONNECT_MESSAGE_TIMER &&
            (isLastClientMessage || isExternalMessage)
        ) {
            await this.customPlugin?.command?.(
                LIVECHAT_ACTION_WEBCHAT_INJECT_MESSAGE,
                this.awayMessageArgs,
            );
            await this.sendDisconnectMessage();
        }
    }

    /**
     * Ends chat, reset session storage and sends analytics
     *
     */
    public endChatRoutine(): void {
        this.userMessages = 0;
        this.sessionStorage.deleteItem("messageAdded");
        this.sessionStorage.deleteItem("launchChatCounter");
        this.sessionStorage.deleteItem("lastClientMessageTimestamp");
        this.sessionStorage.setItem("livechatCompleted", 1);
        this.analyticsService.sendData(
            new LivechatEvent({
                eventType: CLOSE_CHAT,
                eventLabel: this.getEventLabel(),
            }),
        );
    }

    /**
     * Triggers the away timer
     *
     * @param timeDiff : number Time difference ( in ms ) between now and when the message was sent which permits to set the timer to its last state
     */
    public triggerAwayTimer(timeDiff?: number): void {
        let triggerTime: number = AWAY_MESSAGE_TIMER;
        if (timeDiff) {
            triggerTime = AWAY_MESSAGE_TIMER - timeDiff;
        }
        this.subscriptionAwayMessageTimer.add(
            this.appReference.isStable
                .pipe(
                    first((stable) => stable),
                    delay(triggerTime),
                )
                .subscribe(() => {
                    this.sendAwayMessage();
                }),
        );
    }

    public sendAwayMessage(timeDiff?: number): void {
        this.customPlugin
            .command?.(
                LIVECHAT_ACTION_WEBCHAT_INJECT_MESSAGE,
                this.awayMessageArgs,
            )
            .then(() => {
                this.routineAfterInjectedMessage();
                this.triggerDisconnectTimer(timeDiff);
            });
    }

    /**
     * Triggers the disconnect timer
     *
     * @param timeDiff : number Time difference ( in ms ) between now and when the message was sent which permits to set the timer to its last state
     */
    public triggerDisconnectTimer(timeDiff?: number): void {
        let triggerTime: number = DISCONNECT_MESSAGE_TIMER;

        if (timeDiff) {
            triggerTime =
                DISCONNECT_MESSAGE_TIMER + AWAY_MESSAGE_TIMER - timeDiff;
        }

        this.subscriptionDisconnectMessageTimer.add(
            this.appReference.isStable
                .pipe(
                    first((stable) => stable),
                    delay(triggerTime),
                )
                .subscribe(() => this.sendDisconnectMessage()),
        );
    }

    public async sendDisconnectMessage(): Promise<void> {
        await this.customPlugin?.command?.(
            LIVECHAT_ACTION_WEBCHAT_INJECT_MESSAGE,
            this.disconnectMessageArgs,
        );
        this.routineAfterInjectedMessage();
        await this.customPlugin?.command?.(LIVECHAT_ACTION_WEBCHAT_END_CHAT);
    }

    /**
     * Executes specific actions after a custom message is injected
     *
     */
    public routineAfterInjectedMessage(): void {
        const elements = this.document.querySelectorAll(".cx-injected");
        Object.values(elements).forEach((element: Element) => {
            element.classList.add(
                "cx-message",
                "cx-participant",
                "cx-them",
                "cx-Agent",
                "cx-agent",
            );
        });
        this.newMessage = true;
        this.unsubscribeTimers();
        this.changeDetectionEmitter.emit();
    }

    /**
     * Unsubscribes away and disconnect timers
     *
     */
    public unsubscribeTimers(): void {
        if (this.subscriptionAwayMessageTimer) {
            this.subscriptionAwayMessageTimer.unsubscribe();
        }
        if (this.subscriptionDisconnectMessageTimer) {
            this.subscriptionDisconnectMessageTimer.unsubscribe();
        }
    }

    private launchLiveChat(renderer: Renderer2): void {
        // init
        const LIVECHAT_PLUGIN_PATH = `https://apps.${this.livechatConfigUrl}/widgets/9.0/plugins/`;
        this.window.CXBus.configure({
            debug: false,
            pluginsPath: LIVECHAT_PLUGIN_PATH,
        });
        this.window.CXBus.loadPlugin("widgets-core");
        this.customPlugin = this.window.CXBus.registerPlugin("Hermes");

        if (this.customPlugin) {
            this.showButton();

            this.changeDetectionEmitter.emit();

            this.customPlugin?.subscribe(LIVECHAT_ACTION_WEBCHAT_READY, () => {
                // Hide button when open chat
                this.webchatOpenedSubscriber(renderer);

                // Show button when chat is minimized
                this.webchatMinimizedSubscriber();

                // Mask button when chat is unminimized
                this.webchatUnminimizedSubscriber(renderer);

                // Show button after closing chat
                this.webchatClosedSubscriber();

                // Show button after closing chat
                this.webchatSubmittedSubscriber();

                this.webchatMessageAddedSubscriber(renderer);

                this.webchatCompletedSubscriber();

                this.webchatCancelledSubscriber();
            });
        }
    }

    /**
     * Webchat opened callback function
     *
     * @param renderer
     * @private
     */
    private webchatOpenedSubscriber(renderer: Renderer2) {
        this.customPlugin?.subscribe(LIVECHAT_ACTION_WEBCHAT_OPENED, () => {
            // Add content square attributes
            const body = renderer.selectRootElement(".cx-transcript", true);
            const input = renderer.selectRootElement(
                ".cx-input-container",
                true,
            );
            renderer.setAttribute(body, CS_MASK, "");
            renderer.setAttribute(input, CS_MASK, "");

            this.chatStarted = true;
            this.showChatButton = false;
            this.sessionStorage.deleteItem("liveChatButtonDisplayed");

            // Prevent sending launch chat event if it has already been done
            if (!this.sessionStorage.getItem("launchChatCounter")) {
                this.sessionStorage.setItem("launchChatCounter", 1);
                this.analyticsService.sendData(
                    new LivechatEvent({
                        eventType: LAUNCH_CHAT,
                        eventLabel: this.getEventLabel(),
                    }),
                );
            }
            this.editIcons(renderer);
            this.changeDetectionEmitter.emit();
        });
    }

    /**
     * Webchat minimized callback function
     *
     * @private
     */
    private webchatMinimizedSubscriber() {
        this.customPlugin?.subscribe(LIVECHAT_ACTION_WEBCHAT_MINIMIZED, () => {
            this.chatWindowIsMinimized = true;
            this.showChatButton = true;
            this.newMessage = false;
            this.changeDetectionEmitter.emit();
        });
    }

    /**
     * Webchat unminimized callback function
     *
     * @param renderer
     * @private
     */
    private webchatUnminimizedSubscriber(renderer: Renderer2) {
        this.customPlugin?.subscribe(
            LIVECHAT_ACTION_WEBCHAT_UNMINIMIZED,
            () => {
                this.chatWindowIsMinimized = false;
                this.showChatButton = false;
                this.newMessage = false;
                this.sessionStorage.setItem("liveChatAlreadyFlashing", "false");
                this.sessionStorage.deleteItem("liveChatButtonDisplayed");
                this.editIcons(renderer);
                this.changeDetectionEmitter.emit();
            },
        );
    }

    /**
     * Webchat closed callback function
     *
     * @private
     */
    private webchatClosedSubscriber() {
        this.customPlugin?.subscribe(LIVECHAT_ACTION_WEBCHAT_CLOSED, () => {
            this.showChatButton = true;
            this.chatStarted = false;
            this.userMessages = 0;
            this.sessionStorage.deleteItem("messageAdded");
            this.sessionStorage.deleteItem("launchChatCounter");
            this.newMessage = false;
            this.sessionStorage.deleteItem("liveChatAlreadyFlashing");
            this.sessionStorage.deleteItem("liveChatButtonDisplayed");
            this.changeDetectionEmitter.emit();
        });
    }

    /**
     * Webchat submitted callback function
     *
     * @private
     */
    private webchatSubmittedSubscriber() {
        this.customPlugin?.subscribe(LIVECHAT_ACTION_WEBCHAT_SUBMITTED, () => {
            this.analyticsService.sendData(
                new LivechatEvent({
                    eventType: SUBMIT_CHAT,
                    eventLabel: this.getEventLabel(),
                }),
            );
            this.unsubscribeTimers();
            this.triggerAwayTimer();
        });
    }

    /**
     * Webchat message added callback function
     *
     * @param renderer
     * @private
     */
    private webchatMessageAddedSubscriber(renderer: Renderer2) {
        this.customPlugin?.subscribe(
            LIVECHAT_ACTION_WEBCHAT_MESSAGE_ADDED,
            (result: LivechatMessage) => {
                // prevent messages from getting displayed in content square
                this.removeMessagesFromContentSquare(renderer);
                if (
                    this.showChatButton &&
                    result.data?.data?.type === "Agent" &&
                    this.cookieService.get(LIVECHAT_COOKIE_UNREAD_MESSAGE_COUNT)
                ) {
                    this.newMessage = true;
                    this.changeDetectionEmitter.emit();
                }

                if (result.data.message.restoring) {
                    this.restoreMessages(result);
                }

                const isClient = result?.data?.data?.type === "Client";
                if (!isClient) {
                    return;
                }

                if (!result.data.message.restoring) {
                    this.unsubscribeTimers();
                    this.sessionStorage.setItem(
                        "lastClientMessageTimestamp",
                        result.data.message.timestamp,
                    );
                    this.triggerAwayTimer();
                }

                const messageAdded =
                    this.sessionStorage.getItem<number>("messageAdded");
                this.userMessages++;
                if (messageAdded && messageAdded >= this.userMessages) {
                    return;
                }
                this.sessionStorage.setItem("messageAdded", this.userMessages);
                if (this.userMessages === 1) {
                    this.analyticsService.sendData(
                        new LivechatEvent({
                            eventType: FIRST_MESSAGE,
                            eventLabel: this.getEventLabel(),
                        }),
                    );
                    return;
                }
                if (this.userMessages === 5) {
                    this.analyticsService.sendData(
                        new LivechatEvent({
                            eventType: ENGAGED,
                            eventLabel: this.getEventLabel(),
                        }),
                    );
                }
            },
        );
    }

    /**
     * Webchat completed callback function
     *
     * @private
     */
    private webchatCompletedSubscriber() {
        this.customPlugin?.subscribe(LIVECHAT_ACTION_WEBCHAT_COMPLETED, () => {
            this.endChatRoutine();
        });
    }

    /**
     * Webchat cancelled callback function
     *
     * @private
     */
    private webchatCancelledSubscriber() {
        this.customPlugin?.subscribe(LIVECHAT_ACTION_WEBCHAT_CANCELLED, () => {
            this.endChatRoutine();
        });
    }

    /**
     * Show chat button if needed
     * if chat not started or if chat started and chat window minimized
     */
    private showButton() {
        // reset previous timeout to prevent the button to load from the previous page
        if (this.subscriptionTimer) {
            this.subscriptionTimer.unsubscribe();
        }

        if (
            this.sessionStorage.getItem("liveChatButtonDisplayed") &&
            !this.sessionStorage.getItem("livechatCompleted")
        ) {
            this.showChatButton = true;
            return;
        }

        if (
            (this.chatStarted && this.chatWindowIsMinimized) ||
            !this.chatStarted
        ) {
            this.showChatButton = true;
            this.sessionStorage.deleteItem("livechatCompleted");
            this.changeDetectionEmitter.emit();
        }

        // Prevent sending display automatic event if it has already been done
        if (!this.sessionStorage.getItem("liveChatButtonDisplayed")) {
            this.sessionStorage.setItem("liveChatButtonDisplayed", 1);
            this.analyticsService.sendData(
                new LivechatEvent({
                    eventType: DISPLAY_AUTOMATIC_BUBBLE,
                    eventLabel: this.getEventLabel(),
                }),
            );
        }
    }

    private launchLiveChatLight() {
        this.showButton();
        this.changeDetectionEmitter.emit();
    }

    private removeMessagesFromContentSquare(renderer: Renderer2): void {
        try {
            const messageList: HTMLCollectionOf<Element> =
                this.document.getElementsByClassName("cx-message-text");
            for (const message in messageList) {
                if (
                    Object.prototype.hasOwnProperty.call(messageList, message)
                ) {
                    renderer.setAttribute(messageList[message], CS_MASK, "");
                }
            }
            this.changeDetectionEmitter.emit();
        } catch (error) {
            // Livechat css not found, ignoring the error
            this.logger.warn(`Livechat css not found ${error}`);
        }
    }

    private getEventLabel() {
        if (this.location.path().includes("/product/")) {
            return getPageType({ templateType: this.pageType });
        }
        return this.pageType;
    }

    private getPrivacyMessage(): Observable<string> {
        return this.translateService
            .get([PRIVACY_LINK_KEY, PRIVACY_TEXT_KEY])
            .pipe(
                first(),
                map((translations) => {
                    const privacyLinkAnchor = generateLink(
                        translations[PRIVACY_LINK_KEY],
                        PRIVACY_LINK_TEXT,
                    );
                    return translations[PRIVACY_TEXT_KEY].replace(
                        PRIVACY_LINK_KEYWORD,
                        `${privacyLinkAnchor}`,
                    );
                }),
            );
    }

    private replaceLineBreaks(message: string): string {
        return message.replace(/!br\b/g, "<br>");
    }
}
